import { useEffect } from "react";

// app-wide keyboard shortcuts.
//
// Registers a single window-level `keydown` handler that matches user input
// against the shortcut list below and dispatches the corresponding custom
// event. The dialogs (CommandPalette, ShortcutCheatSheet) listen to those
// events directly so callers don't need to manage dialog state.
//
// Events:
//   netbase:openCommandPalette  — triggered by Cmd/Ctrl+K
//   netbase:openCheatSheet      — triggered by Cmd/Ctrl+/ or `?`
//   netbase:forceSync           — triggered by Cmd/Ctrl+S (we intercept the
//                                 native save shortcut so it doesn't save the
//                                 page as an HTML file).
//   netbase:navigate            — dispatched by the `g <letter>` Vim-style
//                                 leader sequences. App.jsx listens and
//                                 routes via react-router.
//
// The same events can be dispatched imperatively (e.g. from the sidebar
// "Help" item) and will trigger the same UI.

// `g <letter>` navigation leader. Targets are wired up in `NAV_TARGETS`; the
// cheat sheet renders these as 2-key sequences with the word "then" between.
const NAV_LEADER_TIMEOUT_MS = 1500;
const NAV_TARGETS = {
  h: { to: "/dashboard", description: "Go to Dashboard" },
  r: { to: "/components/router", description: "Go to Router editor" },
  s: { to: "/components/switch", description: "Go to Switch editor" },
  v: { to: "/components/vlan", description: "Go to VLAN editor" },
  c: { to: "/components/configurations", description: "Go to Configurations" },
  f: { to: "/components/fabric", description: "Go to Fabric" },
  i: { to: "/components/inventory", description: "Go to Inventory" },
  d: { to: "/components/deployment", description: "Go to Deployments" },
  k: { to: "/components/connectors", description: "Go to Connectors" },
  p: { to: "/projects", description: "Go to Projects" }
};

export const SHORTCUTS = [
  {
    id: "palette",
    keys: ["Cmd", "K"],
    altKeys: ["Ctrl", "K"],
    description: "Open the command palette",
  },
  {
    id: "cheatSheetSlash",
    keys: ["Cmd", "/"],
    altKeys: ["Ctrl", "/"],
    description: "Show all keyboard shortcuts",
  },
  {
    id: "cheatSheetQuestion",
    keys: ["?"],
    altKeys: ["?"],
    description: "Show all keyboard shortcuts",
  },
  {
    id: "sectionSearch",
    keys: ["/"],
    altKeys: ["/"],
    description: "Search sections (Router / Switch editors)",
  },
  {
    id: "forceSync",
    keys: ["Cmd", "S"],
    altKeys: ["Ctrl", "S"],
    description: "Flush pending changes to the cloud",
  },
  // Vim-style navigation leader: hit `g`, then within 1.5s hit a target key.
  ...Object.entries(NAV_TARGETS).map(([key, target]) => ({
    id: `nav-${key}`,
    keys: ["G", key.toUpperCase()],
    altKeys: ["G", key.toUpperCase()],
    description: target.description,
    sequence: true
  }))
];

function isTypingTarget(target) {
  if (!target || typeof target !== "object") return false;
  const tag = String(target.tagName || "").toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    target.isContentEditable === true
  );
}

export default function useGlobalShortcuts() {
  useEffect(() => {
    let leaderActive = false;
    let leaderTimeoutId = null;

    const clearLeader = () => {
      leaderActive = false;
      if (leaderTimeoutId !== null) {
        window.clearTimeout(leaderTimeoutId);
        leaderTimeoutId = null;
      }
    };

    const onKeyDown = (event) => {
      const modifier = event.metaKey || event.ctrlKey;

      if (modifier) {
        const key = event.key.toLowerCase();

        // Cmd/Ctrl+K — command palette. Always active; inputs get to keep
        // the keystroke only if they explicitly opt out via
        // data-shortcut="none".
        if (key === "k" && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("netbase:openCommandPalette"));
          clearLeader();
          return;
        }

        if (key === "/" && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("netbase:openCheatSheet"));
          clearLeader();
          return;
        }

        if (key === "s" && !event.shiftKey && !event.altKey) {
          // Always preventDefault so the browser doesn't save the page as
          // HTML, regardless of typing context.
          event.preventDefault();
          window.dispatchEvent(new CustomEvent("netbase:forceSync"));
          clearLeader();
        }

        return;
      }

      // Non-modifier keys below: only fire when the user isn't typing.
      if (isTypingTarget(event.target)) {
        clearLeader();
        return;
      }

      // `?` opens the cheat sheet (Shift+/ on most layouts).
      if (event.key === "?") {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("netbase:openCheatSheet"));
        clearLeader();
        return;
      }

      // Vim-style 2-key navigation: hit `g`, then a target letter within
      // NAV_LEADER_TIMEOUT_MS.
      const key = event.key.toLowerCase();

      if (leaderActive) {
        const target = NAV_TARGETS[key];
        clearLeader();
        if (target) {
          event.preventDefault();
          window.dispatchEvent(
            new CustomEvent("netbase:navigate", { detail: { to: target.to } })
          );
        }
        return;
      }

      if (key === "g" && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        leaderActive = true;
        leaderTimeoutId = window.setTimeout(clearLeader, NAV_LEADER_TIMEOUT_MS);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearLeader();
    };
  }, []);
}
